import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUIStore } from '../../stores/uiStore';
import { pipelineAPI } from '../../services/api';
import './Admin.css';

interface SettingConfig {
  key: string;
  title: string;
  description: string;
  defaultEnabled: boolean;
}

const SETTINGS: SettingConfig[] = [
  {
    key: 'require_stage_approval',
    title: 'Require Stage Approval',
    description: 'Require admin approval before a project can advance to the next pipeline stage.',
    defaultEnabled: false,
  },
  {
    key: 'admin_sees_all_projects',
    title: 'Admin Sees All Projects',
    description: 'Allow admin and super-admin users to view all projects across the organisation.',
    defaultEnabled: true,
  },
  {
    key: 'trial_member_can_lead',
    title: 'Trial Member Can Lead',
    description: 'Allow trial-tier members to be assigned as project leads.',
    defaultEnabled: false,
  },
  {
    key: 'default_structure_mask',
    title: 'Default Structure Masking',
    description: 'Mask chemical structures by default for new team members added to projects.',
    defaultEnabled: false,
  },
];

const OrgSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const showNotification = useUIStore((s) => s.showNotification);
  const [settings, setSettings] = useState<Record<string, Record<string, unknown>>>({});
  const [loading, setLoading] = useState(true);

  // Redirect non-admin users
  useEffect(() => {
    if (user && user.user_type !== 'admin' && user.user_type !== 'super_admin') {
      navigate('/pipeline', { replace: true });
    }
  }, [user, navigate]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await pipelineAPI.getOrgSettings();
      const data: Record<string, Record<string, unknown>> = {};
      for (const item of res.data) {
        data[item.setting_key] = item.setting_val;
      }
      setSettings(data);
    } catch (err) {
      console.error('Failed to fetch org settings:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const isEnabled = (key: string, defaultVal: boolean): boolean => {
    const val = settings[key];
    if (val && typeof val.enabled === 'boolean') return val.enabled;
    return defaultVal;
  };

  const handleToggle = async (cfg: SettingConfig) => {
    const currentValue = isEnabled(cfg.key, cfg.defaultEnabled);
    const newValue = !currentValue;

    // Optimistic update
    setSettings((prev) => ({
      ...prev,
      [cfg.key]: { ...prev[cfg.key], enabled: newValue },
    }));

    try {
      await pipelineAPI.updateOrgSetting(cfg.key, { enabled: newValue });
      showNotification('success', `${cfg.title} ${newValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      // Revert on error
      setSettings((prev) => ({
        ...prev,
        [cfg.key]: { ...prev[cfg.key], enabled: currentValue },
      }));
      showNotification('error', `Failed to update ${cfg.title}`);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="admin-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {SETTINGS.map((cfg) => {
        const enabled = isEnabled(cfg.key, cfg.defaultEnabled);
        return (
          <div
            key={cfg.key}
            className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between"
          >
            <div className="flex-1 mr-4">
              <h3 className="text-sm font-semibold text-gray-800">{cfg.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{cfg.description}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => handleToggle(cfg)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default OrgSettingsPage;
